/**
 * SEO — reusable Helmet wrapper for per-page <title>, meta description,
 * and Open Graph / Twitter Card tags. Keeps title under 60 chars and
 * description under 160 chars per Google guidelines.
 *
 * Backward-compat: accepts both `path`/`image`/`type` (legacy callers) AND
 * `canonical`/`ogImage`/`ogType` (newer callers). Won't double-suffix the
 * site name if the title already includes it.
 */
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'IB Club India';
const DEFAULT_DESCRIPTION = 'Premium global community for investors, entrepreneurs, and business leaders in India. Network, discover deals, and access exclusive member benefits.';
const DEFAULT_OG_IMAGE = '/og-image.jpg';
const SITE_URL = 'https://ibclub.org';

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  // New canonical props
  canonical,
  ogImage,
  ogType,
  // Legacy props
  image,
  path,
  type,
  jsonLd = null,
  noIndex = false,
}) => {
  // Title: don't double-append "IB Club India" if already present.
  const baseTitle = (title || '').trim();
  const fullTitle = !baseTitle
    ? SITE_NAME
    : (baseTitle.toLowerCase().includes(SITE_NAME.toLowerCase())
        ? baseTitle
        : `${baseTitle} · ${SITE_NAME}`);

  // Canonical URL — prefer explicit `canonical`, else `path` (legacy), else current path
  let url;
  if (canonical) {
    url = canonical;
  } else if (path !== undefined) {
    url = `${SITE_URL}${path}`;
  } else if (typeof window !== 'undefined') {
    url = window.location.origin + window.location.pathname;
  } else {
    url = SITE_URL;
  }

  // Image — prefer explicit `ogImage`, then `image` (legacy), then default
  const safeImage = ogImage || image || DEFAULT_OG_IMAGE;
  const absImage = safeImage.startsWith('http') ? safeImage : `${SITE_URL}${safeImage}`;

  const ogTypeFinal = ogType || type || 'website';

  // Truncate description per Google guideline (~160 chars)
  const safeDesc = (description || DEFAULT_DESCRIPTION).slice(0, 160);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={safeDesc} />
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={safeDesc} />
      <meta property="og:image" content={absImage} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={ogTypeFinal} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={safeDesc} />
      <meta name="twitter:image" content={absImage} />

      {/* Structured data */}
      {jsonLd && Array.isArray(jsonLd) && jsonLd.map((blob, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(blob)}
        </script>
      ))}
      {jsonLd && !Array.isArray(jsonLd) && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
